import dayjs, {Dayjs} from 'dayjs';
import {DateRange} from '@mui/lab';
import {Add} from '@mui/icons-material';
import {useSelector} from 'react-redux';
import {useCallback, useMemo, useState} from 'react';
import {IconButton, List, ListItem} from '@mui/material';
import {DatePicker, DateRangePicker} from '@mui/x-date-pickers-pro';
import CustomCalIcon from '../../../../../asset/icons/icon_calendar_gray.svg';
import {isInternalSelector} from '../../../../../store/Slices/userSlice';
import {iTimedMsgDateRange} from '../TimedMessageRequest';

const TimedMsgDateRange = ({
  isOneDay,
  dates,
  onChangeDates,
}: {
  isOneDay: boolean;
  dates: iTimedMsgDateRange[];
  onChangeDates: (value: React.SetStateAction<iTimedMsgDateRange[]>) => void;
}) => {
  const isStaff = useSelector(isInternalSelector);
  const minDate = isStaff ? dayjs() : dayjs().add(1, 'day');

  const [datesState, setDatesState] = useState<DateRange<Dayjs | null>>([
    null,
    null,
  ]);

  const handleOnChange = useCallback((dates: DateRange<Dayjs | null>) => {
    setDatesState(dates);
  }, []);

  const handleSingleDate = useCallback((val: Dayjs | null) => {
    setDatesState([val, val]);
  }, []);

  const handleAdd = useCallback(() => {
    onChangeDates(prevDateRanges => {
      const newData = {
        id: -1 * Date.now(),
        start_date: datesState[0],
        end_date: datesState[1],
        chosenDays: isOneDay ? [dayjs(datesState[0]).format('dddd')] : [],
        day_times: [
          {
            id: -1 * (Date.now() + 1),
            selectedDays: [],
            timeRanges: [],
          },
        ],
      };

      let updatedRanges = [];

      updatedRanges =
        !prevDateRanges[0]?.start_date && !prevDateRanges[0]?.end_date
          ? [newData]
          : [newData, ...prevDateRanges];

      // Sort by start_date ascending
      return updatedRanges.sort(
        (a, b) =>
          (a.start_date?.valueOf() || 0) - (b.start_date?.valueOf() || 0),
      );
    });
    setDatesState([null, null]);
  }, [datesState, isOneDay, onChangeDates]);
  function MuiIcon() {
    return (
      <img src={CustomCalIcon} alt='Date picker opening icon' width={23} />
    );
  }

  const disableAdd = useMemo(() => {
    if (!isOneDay && dates.length > 9) {
      // Once the dates range reaches 10, disable the add button
      return true;
    } else if (datesState.some(d => !d)) {
      return true;
    } else if (
      dates.every(o => o.start_date && o.end_date) &&
      dates.some(
        o =>
          (o.start_date &&
            o.start_date.isBetween(datesState[0], datesState[1], 'd', '[]')) ||
          (o.end_date &&
            o.end_date.isBetween(datesState[0], datesState[1], 'd', '[]')) ||
          datesState[0]?.isBetween(o.start_date, o.end_date, 'd', '[]') ||
          datesState[1]?.isBetween(o.start_date, o.end_date, 'd', '[]'),
      )
    ) {
      return true;
    }
    return false;
  }, [dates, datesState, isOneDay]);

  return (
    <List>
      <ListItem className='w-340' disableGutters>
        {isOneDay ? (
          <DatePicker
            className='w-175'
            disablePast
            minDate={minDate}
            label='Date'
            value={datesState[0]}
            slots={{openPickerIcon: MuiIcon}}
            onChange={handleSingleDate}
            slotProps={{
              textField: {
                size: 'small',
                InputLabelProps: {
                  shrink: true,
                  className: 'inputLabel-shink-background',
                },
              },
              calendarHeader: {className: 'month-year'},
              popper: {'aria-label': 'Timed message single date dialog'},
            }}
          />
        ) : (
          <DateRangePicker
            className='max-w-320'
            disablePast
            minDate={minDate}
            localeText={{start: 'Start Date', end: 'End Date'}}
            value={datesState}
            onChange={handleOnChange}
            slotProps={{
              textField: {
                InputLabelProps: {
                  shrink: true,
                  className: 'inputLabel-shink-background',
                },
              },
              popper: {'aria-label': 'Timed message date range dialog'},
            }}
          />
        )}

        <IconButton
          className='ml-4 w-40 h-40 font-bold radius-5'
          sx={{
            background: `var(--blue-variant10)`,
            border: `1px solid var(--blue_variant40)`,
          }}
          aria-label='add-time'
          disabled={disableAdd}
          onClick={handleAdd}>
          <Add />
        </IconButton>
      </ListItem>
    </List>
  );
};

export default TimedMsgDateRange;
